// In-memory state for one Call-to-Claim run: the uploaded file, the transcript,
// and the editable AnswerSet. Mirrors the Magic Capture flow-context pattern.

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AaAnswerValue, AnswerSet } from './lib/types';

interface ClaimFlowState {
  fileName: string | null;
  transcript: string | null;
  answers: AnswerSet;
  startRun: (fileName: string) => void;
  setTranscript: (t: string) => void;
  setAnswers: (a: AnswerSet) => void;
  /** Advisor edits a field: updates the value and marks it user-sourced (full confidence). */
  editAnswer: (questionId: string, value: AaAnswerValue | null) => void;
  reset: () => void;
}

/**
 * Coerce contentless edits to null so "filled" checks, the required-fields
 * download gate, and the edited/confidence chips never count an unticked
 * multiselect ([]) or a blanked address ({addressLine1: ''}) as an answer.
 */
function normalizeValue(v: AaAnswerValue | null): AaAnswerValue | null {
  if (v === '') return null;
  if (Array.isArray(v)) return v.length === 0 ? null : v;
  if (v && typeof v === 'object') {
    return Object.values(v).some((s) => String(s ?? '').trim()) ? v : null;
  }
  return v;
}

const ClaimFlowContext = createContext<ClaimFlowState | null>(null);

export function ClaimFlowProvider({ children }: { children: ReactNode }) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [answers, setAnswers] = useState<AnswerSet>({});

  const value = useMemo<ClaimFlowState>(
    () => ({
      fileName,
      transcript,
      answers,
      startRun: (name) => {
        setFileName(name);
        setTranscript(null);
        setAnswers({});
      },
      setTranscript,
      setAnswers,
      editAnswer: (questionId, v) =>
        setAnswers((prev) => ({
          ...prev,
          [questionId]: {
            questionId,
            value: normalizeValue(v),
            confidence: 'high',
            sourceQuotes: prev[questionId]?.sourceQuotes ?? [],
            edited: true,
          },
        })),
      reset: () => {
        setFileName(null);
        setTranscript(null);
        setAnswers({});
      },
    }),
    [fileName, transcript, answers],
  );

  return (
    <ClaimFlowContext.Provider value={value}>
      {children}
    </ClaimFlowContext.Provider>
  );
}

export function useClaimFlow(): ClaimFlowState {
  const ctx = useContext(ClaimFlowContext);
  if (!ctx)
    throw new Error('useClaimFlow must be used within ClaimFlowProvider');
  return ctx;
}
